import React, { useState, useEffect } from 'react';
import { walletApi } from '../services/walletApi';

const WalletGroupManager = ({ onGroupSelect, selectedGroupId, onUpdateWallets, onGroupsUpdate, tokenId }) => {
  const [groups, setGroups] = useState([]);
  const [allWallets, setAllWallets] = useState([]);
  const [ungroupedWallets, setUngroupedWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#9333EA');
  const [editingGroup, setEditingGroup] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [colorPickerOpen, setColorPickerOpen] = useState(null);
  
  // Fetch wallet groups and calculate actual counts
  const fetchGroups = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel with token context
      const [groupsResponse, allWalletsResponse, ungroupedResponse] = await Promise.all([
        walletApi.getWalletGroupsWithStatus(tokenId).catch(() => walletApi.getWalletGroups(tokenId).catch(() => [])),
        walletApi.getUserWallets(null, tokenId).catch(() => ({ wallets: [] })),
        walletApi.getUngroupedWallets(tokenId).catch(() => ({ wallets: [] }))
      ]);
      
      const fetchedGroups = Array.isArray(groupsResponse) ? groupsResponse : [];
      const allWalletsData = allWalletsResponse.wallets || [];
      const ungroupedData = ungroupedResponse.wallets || [];
      
      // Set the data
      setGroups(fetchedGroups);
      setAllWallets(allWalletsData);
      setUngroupedWallets(ungroupedData);

      // Update parent with groups data
      if (onGroupsUpdate) {
        onGroupsUpdate(fetchedGroups);
      }

      setLoading(false);
      
      // Only auto-select if no group is currently selected AND it's the initial load
      if (selectedGroupId === undefined && fetchedGroups.length > 0 && onGroupSelect) {
        onGroupSelect(fetchedGroups[0].id);
      }
    } catch (error) {
      console.error('Error fetching wallet groups:', error);
      setError('Failed to load wallet groups. Please try again.');
      setLoading(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    fetchGroups();
  }, []);

  // Close color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerOpen && !event.target.closest('.color-picker-container')) {
        setColorPickerOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [colorPickerOpen]);
  
  // Create a new group
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    if (!newGroupName.trim()) {
      setError('Group name is required');
      return;
    }
    
    try {
      setLoading(true);
      
      const newGroup = await walletApi.createWalletGroup({
        name: newGroupName,
        description: newGroupDescription,
        color: newGroupColor
      }, tokenId);
      
      // Refresh groups
      await fetchGroups();
      
      // Reset form
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupColor('#9333EA');
      setShowCreateForm(false);
      
      // Select the new group
      if (onGroupSelect) {
        onGroupSelect(newGroup.id);
      }
      
      // Notify parent to refresh wallets if needed
      if (onUpdateWallets) {
        onUpdateWallets();
      }
    } catch (error) {
      console.error('Error creating wallet group:', error);
      setError('Failed to create group. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Update a group
  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    
    if (!editingGroup || !editingGroup.name.trim()) {
      setError('Group name is required');
      return;
    }
    
    try {
      setLoading(true);
      
      await walletApi.updateWalletGroup(
        editingGroup.id,
        {
          name: editingGroup.name,
          description: editingGroup.description,
          is_default: editingGroup.is_default
        }
      );
      
      // Refresh groups
      await fetchGroups();
      
      // Reset editing state
      setEditingGroup(null);
      
      // Notify parent to refresh wallets if needed
      if (onUpdateWallets) {
        onUpdateWallets();
      }
    } catch (error) {
      console.error('Error updating wallet group:', error);
      setError('Failed to update group. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Delete a group
  const handleDeleteGroup = async (groupId) => {
    try {
      setLoading(true);
      
      await walletApi.deleteWalletGroup(groupId);
      
      // Refresh groups
      await fetchGroups();
      
      // If the deleted group was selected, select another one
      if (selectedGroupId === groupId && groups.length > 1) {
        const remainingGroups = groups.filter(g => g.id !== groupId);
        if (remainingGroups.length > 0 && onGroupSelect) {
          onGroupSelect(remainingGroups[0].id);
        } else if (onGroupSelect) {
          onGroupSelect(null);
        }
      }
      
      // Reset confirmation state
      setConfirmDelete(null);
      
      // Notify parent to refresh wallets if needed
      if (onUpdateWallets) {
        onUpdateWallets();
      }
    } catch (error) {
      console.error('Error deleting wallet group:', error);
      setError('Failed to delete group. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Set a group as default
  const handleSetDefaultGroup = async (groupId) => {
    try {
      setLoading(true);
      
      const group = groups.find(g => g.id === groupId);
      if (!group) return;
      
      await walletApi.updateWalletGroup(
        groupId,
        {
          is_default: true
        }
      );
      
      // Refresh groups
      await fetchGroups();
      
      // Notify parent to refresh wallets if needed
      if (onUpdateWallets) {
        onUpdateWallets();
      }
    } catch (error) {
      console.error('Error setting default wallet group:', error);
      setError('Failed to update default group. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Quick color change
  const handleQuickColorChange = async (groupId, newColor) => {
    try {
      // Find the group
      const group = groups.find(g => g.id === groupId);
      if (!group) return;

      // Update the group color
      await walletApi.updateWalletGroup(groupId, {
        name: group.name,
        description: group.description,
        color: newColor,
        is_default: group.is_default
      });

      // Refresh groups
      await fetchGroups();
      
      // Close color picker
      setColorPickerOpen(null);
      
      // Notify parent if needed
      if (onUpdateWallets) {
        onUpdateWallets();
      }
    } catch (error) {
      console.error('Error updating group color:', error);
      setError('Failed to update group color. Please try again.');
    }
  };
  
  // Calculate total wallets
  const totalWallets = allWallets.length;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">Wallet Groups</h2>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
        >
          {showCreateForm ? 'Cancel' : '+ New Group'}
        </button>
      </div>
      
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 p-3 rounded-lg mb-4 text-sm transition-colors">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="ml-2 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-200"
          >
            ×
          </button>
        </div>
      )}
      
      {/* Create Group Form */}
      {showCreateForm && (
        <form onSubmit={handleCreateGroup} className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Create New Group</h3>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <input
                type="text"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Group Name"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                required
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                value={newGroupDescription}
                onChange={(e) => setNewGroupDescription(e.target.value)}
                placeholder="Description (optional)"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Group Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={newGroupColor}
                  onChange={(e) => setNewGroupColor(e.target.value)}
                  className="w-12 h-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors shadow-sm"
                  title="Choose group color"
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm"
                  style={{ backgroundColor: newGroupColor }}
                ></div>
                <input
                  type="text"
                  value={newGroupColor}
                  onChange={(e) => {
                    if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                      setNewGroupColor(e.target.value);
                    }
                  }}
                  className="w-20 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded-md font-mono focus:border-purple-400 focus:ring-1 focus:ring-purple-100 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="#9333EA"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-3 py-2 text-sm bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-purple-400"
            >
              {loading ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      )}
      
      {/* Edit Group Form */}
      {editingGroup && (
        <form onSubmit={handleUpdateGroup} className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Edit Group</h3>
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1">
              <input
                type="text"
                value={editingGroup.name}
                onChange={(e) => setEditingGroup({...editingGroup, name: e.target.value})}
                placeholder="Group Name"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                required
              />
            </div>
            <div className="flex-1">
              <input
                type="text"
                value={editingGroup.description || ''}
                onChange={(e) => setEditingGroup({...editingGroup, description: e.target.value})}
                placeholder="Description (optional)"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Group Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={editingGroup.color || '#9333EA'}
                  onChange={(e) => setEditingGroup({...editingGroup, color: e.target.value})}
                  className="w-12 h-10 border-2 border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors shadow-sm"
                  title="Choose group color"
                />
                <div 
                  className="w-8 h-8 rounded-lg border-2 border-gray-200 dark:border-gray-600 shadow-sm"
                  style={{ backgroundColor: editingGroup.color || '#9333EA' }}
                ></div>
                <input
                  type="text"
                  value={editingGroup.color || '#9333EA'}
                  onChange={(e) => {
                    if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                      setEditingGroup({...editingGroup, color: e.target.value});
                    }
                  }}
                  className="w-20 px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded-md font-mono focus:border-purple-400 focus:ring-1 focus:ring-purple-100 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="#9333EA"
                />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center text-sm text-gray-900 dark:text-gray-100">
              <input
                type="checkbox"
                checked={editingGroup.is_default}
                onChange={(e) => setEditingGroup({...editingGroup, is_default: e.target.checked})}
                className="mr-2"
              />
              Set as default group
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setEditingGroup(null)}
                className="px-3 py-2 text-sm bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-3 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:bg-purple-400"
              >
                {loading ? 'Updating...' : 'Update Group'}
              </button>
            </div>
          </div>
        </form>
      )}
      
      {/* Delete Confirmation */}
      {confirmDelete && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-700 transition-colors">
          <p className="text-sm text-red-700 dark:text-red-300 mb-3">
            Are you sure you want to delete the group "{confirmDelete.name}"? 
            This will not delete the wallets in the group.
          </p>
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setConfirmDelete(null)}
              className="px-3 py-2 text-sm bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleDeleteGroup(confirmDelete.id)}
              disabled={loading}
              className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:bg-red-400"
            >
              {loading ? 'Deleting...' : 'Delete Group'}
            </button>
          </div>
        </div>
      )}
      
      {/* Group List - Horizontal Layout */}
      <div className="flex flex-wrap gap-3">
        {/* All Wallets Option */}
        <button 
          className={`flex items-center px-4 py-3 rounded-lg border-2 transition-all font-medium
            ${selectedGroupId === null 
              ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-400 dark:border-purple-600 text-purple-700 dark:text-purple-300' 
              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
          onClick={() => {
            console.log('All Wallets clicked, calling onGroupSelect with null');
            if (onGroupSelect) {
              onGroupSelect(null);
            }
          }}
        >
          <div className="w-4 h-4 bg-gray-400 rounded-full mr-2"></div>
          <span>All Wallets</span>
          <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full">{totalWallets}</span>
        </button>
        
        {/* Ungrouped Wallets Option */}
        <button 
          className={`flex items-center px-4 py-3 rounded-lg border-2 transition-all font-medium
            ${selectedGroupId === 'ungrouped' 
              ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-400 dark:border-purple-600 text-purple-700 dark:text-purple-300' 
              : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
          onClick={() => {
            console.log('Ungrouped Wallets clicked, calling onGroupSelect with ungrouped');
            if (onGroupSelect) {
              onGroupSelect('ungrouped');
            }
          }}
        >
          <div className="w-4 h-4 bg-gray-500 rounded-full mr-2"></div>
          <span>Ungrouped</span>
          <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full">{ungroupedWallets.length}</span>
        </button>
        
        {/* Group Cards */}
        {groups.map(group => (
          <div 
            key={group.id}
            className={`flex items-center px-4 py-3 rounded-lg border-2 transition-all font-medium cursor-pointer
              ${selectedGroupId === group.id 
                ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-400 dark:border-purple-600 text-purple-700 dark:text-purple-300' 
                : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 dark:hover:border-gray-500'}`}
            onClick={(e) => {
              if (!e.target.closest('button') && onGroupSelect) {
                console.log(`Group ${group.name} clicked, calling onGroupSelect with ${group.id}`);
                onGroupSelect(group.id);
              }
            }}
          >
            <div className="relative mr-2">
              <div 
                className="w-4 h-4 rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-purple-300 transition-all" 
                style={{ backgroundColor: group.color || '#9333EA' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setColorPickerOpen(colorPickerOpen === group.id ? null : group.id);
                }}
                title="Click to change color"
              ></div>
              {colorPickerOpen === group.id && (
                <div className="absolute top-6 left-0 z-50 bg-white dark:bg-gray-800 border-0 rounded-xl shadow-xl p-4 color-picker-container min-w-[280px] transition-colors" 
                     style={{
                       background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                       boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)'
                     }}>
                  <div className="flex flex-col gap-4">
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                      Choose Color
                    </div>
                    
                    {/* Professional Color Palette - Business & Finance Theme */}
                    <div className="grid grid-cols-6 gap-2">
                      {[
                        // Row 1: Blues & Teals (Professional)
                        '#1E40AF', '#0EA5E9', '#0891B2', '#164E63', '#075985', '#0C4A6E',
                        // Row 2: Purples & Violets (Premium)
                        '#7C3AED', '#9333EA', '#A855F7', '#6366F1', '#4F46E5', '#3730A3',
                        // Row 3: Greens (Growth & Success)
                        '#059669', '#0D9488', '#10B981', '#16A34A', '#15803D', '#166534',
                        // Row 4: Oranges & Reds (Energy & Action)
                        '#EA580C', '#DC2626', '#B91C1C', '#EF4444', '#F97316', '#D97706',
                        // Row 5: Grays & Neutrals (Professional)
                        '#374151', '#4B5563', '#6B7280', '#9CA3AF', '#64748B', '#475569',
                        // Row 6: Special Colors (Finance & Tech)
                        '#BE185D', '#C2410C', '#92400E', '#365314', '#1F2937', '#111827'
                      ].map((color, index) => (
                        <div
                          key={color}
                          className="relative group"
                        >
                          <div
                            className="w-8 h-8 rounded-lg cursor-pointer transition-all duration-200 transform group-hover:scale-110 group-hover:shadow-lg border border-gray-200 group-hover:border-gray-300"
                            style={{ backgroundColor: color }}
                            onClick={() => handleQuickColorChange(group.id, color)}
                          >
                            {(group.color || '#9333EA') === color && (
                              <div className="absolute inset-0 rounded-lg border-2 border-white shadow-md flex items-center justify-center">
                                <svg className="w-4 h-4 text-white drop-shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Custom Color Picker */}
                    <div className="pt-3 border-t border-gray-100">
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Custom Color</div>
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={group.color || '#9333EA'}
                          onChange={(e) => handleQuickColorChange(group.id, e.target.value)}
                          className="w-12 h-8 border-2 border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:border-gray-300 dark:hover:border-gray-500 transition-colors"
                        />
                        <input
                          type="text"
                          value={group.color || '#9333EA'}
                          onChange={(e) => {
                            if (/^#[0-9A-Fa-f]{6}$/.test(e.target.value)) {
                              handleQuickColorChange(group.id, e.target.value);
                            }
                          }}
                          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:border-purple-400 focus:ring-2 focus:ring-purple-100 font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="#9333EA"
                        />
                      </div>
                    </div>
                    
                    {/* Close Button */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setColorPickerOpen(null);
                      }}
                      className="self-end px-4 py-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
            <span>{group.name}</span>
            {group.is_default && (
              <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-1 rounded-full">Default</span>
            )}
            {/* Bot Status Indicator */}
            {group.has_active_bot && (
              <span className={`ml-2 text-xs px-2 py-1 rounded-full flex items-center ${
                group.bot_status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                <span className="w-2 h-2 rounded-full mr-1 animate-pulse" 
                  style={{ backgroundColor: group.bot_status === 'active' ? '#10b981' : '#f59e0b' }}
                ></span>
                Bot {group.bot_status}
              </span>
            )}
            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-1 rounded-full">{group.wallet_count || 0}</span>
            
            {/* Group Actions */}
            <div className="flex ml-2 sm:ml-3 space-x-1 flex-shrink-0">
              {!group.is_default && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSetDefaultGroup(group.id);
                  }}
                  className="p-1 text-gray-500 dark:text-gray-400 hover:text-purple-700 dark:hover:text-purple-300 rounded-full hover:bg-white dark:hover:bg-gray-600"
                  title="Set as default"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingGroup(group);
                }}
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-blue-700 dark:hover:text-blue-300 rounded-full hover:bg-white dark:hover:bg-gray-600"
                title="Edit group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setConfirmDelete(group);
                }}
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-300 rounded-full hover:bg-white dark:hover:bg-gray-600"
                title="Delete group"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        ))}
        
        {groups.length === 0 && !loading && (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 transition-colors">
            No wallet groups found. Create one to organize your wallets.
          </div>
        )}
        
        {loading && (
          <div className="p-4 flex justify-center">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletGroupManager;